(() => {
  if (window.NubsListPagination) return;

  const controllers = new WeakMap();

  function positiveInteger(value, fallback = 1) {
    const number = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  function attach(container, {
    itemSelector = ':scope > article',
    pageSize = 20,
    queryParam = 'list_page',
    label = 'List pagination',
  } = {}) {
    if (!container) return null;
    controllers.get(container)?.destroy?.();

    const size = Math.max(5, Math.min(100, positiveInteger(pageSize, 20)));
    const nav = document.createElement('nav');
    nav.className = 'nubs-list-pagination';
    nav.setAttribute('aria-label', label);
    nav.hidden = true;
    container.insertAdjacentElement('afterend', nav);

    let suppressObserver = false;

    function items() {
      try { return [...container.querySelectorAll(itemSelector)]; }
      catch (_) { return [...container.children].filter((node) => node.matches?.('article')); }
    }

    function requestedPage() {
      return positiveInteger(new URL(window.location.href).searchParams.get(queryParam), 1);
    }

    function writePage(page, {push = true} = {}) {
      const url = new URL(window.location.href);
      if (page <= 1) url.searchParams.delete(queryParam);
      else url.searchParams.set(queryParam, String(page));
      const next = `${url.pathname}${url.search}${url.hash}`;
      if (push) window.history.pushState({}, '', next);
      else window.history.replaceState({}, '', next);
    }

    function button(text, target, {active = false, disabled = false} = {}) {
      const node = document.createElement('button');
      node.type = 'button';
      node.className = 'nubs-list-pagination__button';
      node.textContent = text;
      node.dataset.listPage = String(target);
      node.disabled = disabled;
      if (active) {
        node.classList.add('is-active');
        node.setAttribute('aria-current', 'page');
      }
      return node;
    }

    function render({replaceInvalidUrl = true} = {}) {
      const rows = items();
      const pageCount = Math.max(1, Math.ceil(rows.length / size));
      const requested = requestedPage();
      const current = Math.min(requested, pageCount);
      if (replaceInvalidUrl && current !== requested) writePage(current, {push:false});

      suppressObserver = true;
      rows.forEach((row, index) => {
        row.hidden = index < (current - 1) * size || index >= current * size;
      });
      suppressObserver = false;

      nav.replaceChildren();
      nav.hidden = rows.length <= size;
      if (nav.hidden) return current;

      nav.append(button('Previous', Math.max(1, current - 1), {disabled: current <= 1}));
      const candidates = new Set([1, pageCount, current - 2, current - 1, current, current + 1, current + 2]
        .filter((value) => value >= 1 && value <= pageCount));
      let previous = 0;
      [...candidates].sort((a, b) => a - b).forEach((page) => {
        if (previous && page > previous + 1) {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'nubs-list-pagination__ellipsis';
          ellipsis.textContent = '…';
          ellipsis.setAttribute('aria-hidden', 'true');
          nav.append(ellipsis);
        }
        nav.append(button(String(page), page, {active: page === current}));
        previous = page;
      });
      nav.append(button('Next', Math.min(pageCount, current + 1), {disabled: current >= pageCount}));
      return current;
    }

    function onClick(event) {
      const target = event.target.closest('[data-list-page]');
      if (!target || target.disabled) return;
      event.preventDefault();
      const page = positiveInteger(target.dataset.listPage, 1);
      writePage(page);
      render();
      container.scrollIntoView({behavior:'smooth', block:'start'});
    }

    function onPopState() { render({replaceInvalidUrl:false}); }
    nav.addEventListener('click', onClick);
    window.addEventListener('popstate', onPopState);

    const observer = new MutationObserver(() => {
      if (suppressObserver) return;
      window.requestAnimationFrame(() => render());
    });
    observer.observe(container, {childList:true});

    const controller = {
      render,
      destroy() {
        observer.disconnect();
        nav.removeEventListener('click', onClick);
        window.removeEventListener('popstate', onPopState);
        nav.remove();
        controllers.delete(container);
      },
    };
    controllers.set(container, controller);
    render();
    return controller;
  }

  window.NubsListPagination = {attach};
})();
